import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const LegacyScoreChart = ({ selectedManager, legacyData = [], finalData = [], h2hData = [], className = "" }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const processLegacyData = () => {
      try {
        setLoading(true);
        setError(null);

        if (!legacyData || legacyData.length === 0) {
          throw new Error('No legacy data available');
        }

        if (!selectedManager) {
          throw new Error('No manager selected');
        }

        // Get all years and managers
        const allYears = [...new Set(legacyData.map(row => row.Year))].sort((a, b) => a - b);
        const allManagers = [...new Set(legacyData.map(row => row.Manager))];

        console.log('Legacy data processing:', {
          totalRecords: legacyData.length,
          years: allYears,
          managers: allManagers,
          selectedManager,
          sampleRecord: legacyData[0]
        });

        // Create chart data for each year
        const chartDataByYear = allYears.map(year => {
          const yearData = { Year: year };
          
          // Add data for all managers (for background lines)
          allManagers.forEach(manager => {
            const managerRecord = legacyData.find(row => row.Year === year && row.Manager === manager);
            yearData[manager] = managerRecord ? (managerRecord.LegacyScore || 0) : null;
          });
          
          return yearData;
        });

        // Filter out years where selected manager has no data
        const filteredChartData = chartDataByYear.filter(yearData => yearData[selectedManager] !== null);

        console.log('Processed chart data:', filteredChartData);

        setChartData(filteredChartData);
        setLoading(false);

      } catch (err) {
        console.error('Error processing legacy data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    processLegacyData();
  }, [selectedManager, legacyData]);

  const formatTooltip = (value, name, props) => {
    if (value === null || value === undefined) return null;
    
    const formattedValue = Math.round(value);
    
    if (name === selectedManager) {
      return [`${formattedValue}`, selectedManager];
    } else {
      return [`${formattedValue}`, name];
    }
  };

  const formatYAxis = (value) => {
    return Math.round(value).toString();
  };

  // Calculate significant changes for annotations with season details
  const getSignificantChanges = () => {
    if (!chartData.length || chartData.length < 2) return [];
    
    const managerScores = chartData.map(d => ({
      year: d.Year,
      score: d[selectedManager]
    })).filter(d => d.score !== null && d.score !== undefined);

    if (managerScores.length < 2) return [];

    const changes = [];
    for (let i = 1; i < managerScores.length; i++) {
      const prev = managerScores[i - 1];
      const curr = managerScores[i];
      const change = curr.score - prev.score;
      
      // Get season details for this year
      const seasonData = getSeasonDetails(curr.year, selectedManager);
      
      changes.push({
        year: curr.year,
        score: curr.score,
        change: change,
        prevScore: prev.score,
        isSignificant: Math.abs(change) >= 150, // Threshold for significant changes
        seasonDetails: seasonData
      });
    }

    return changes.filter(c => c.isSignificant);
  };

  // Helper function to get season details for a manager in a specific year
  const getSeasonDetails = (year, manager) => {
    // Get final standings data for this manager/year
    const seasonRecord = finalData.find(row => 
      row.Year === year && 
      (row.Owner === manager || row.Manager === manager)
    );

    // Get regular season record from h2h data
    const seasonGames = h2hData.filter(row => 
      row.Year === year && 
      row.Manager1 === manager && 
      row.Type === 'Regular Season'
    );

    const wins = seasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
    const losses = seasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);
    const totalPF = seasonGames.reduce((sum, game) => sum + (game.PF || 0), 0);

    return {
      wins: wins,
      losses: losses,
      record: `${wins}-${losses}`,
      standings: seasonRecord ? seasonRecord.Standings : null,
      totalPF: totalPF > 0 ? Math.round(totalPF) : null,
      hasData: seasonRecord || seasonGames.length > 0
    };
  };

  const significantChanges = getSignificantChanges();

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="mb-4 sm:mb-6">
          <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'}`}>
            {isMobile ? `${selectedManager}'s Legacy Score` : `${selectedManager}'s Legacy Score Over Time`}
          </h3>
          <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile ? 'Score progression by year' : 'Cumulative legacy score progression by year'}
          </p>
        </div>
        
        <div className="flex items-center justify-center" style={{ height: isMobile ? 300 : 400 }}>
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            <div className={`border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin ${
              isMobile ? 'w-8 h-8' : 'w-12 h-12'
            }`}></div>
            <span className={`text-gray-600 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
              Loading legacy score data...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-red-500 py-8 sm:py-12">
          <div className={isMobile ? 'text-sm' : 'text-base'}>Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-gray-500 py-8 sm:py-12">
          <div className={isMobile ? 'text-sm' : 'text-base'}>
            No legacy score data found for {selectedManager}
          </div>
        </div>
      </div>
    );
  }

  // Get all managers for background lines
  const allManagers = [...new Set(legacyData.map(row => row.Manager))];
  const otherManagers = allManagers.filter(manager => manager !== selectedManager);

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title - Mobile Optimized */}
      <div className="mb-4 sm:mb-6">
        <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'}`}>
          {isMobile ? `${selectedManager}'s Legacy Score` : `${selectedManager}'s Legacy Score Over Time`}
        </h3>
        <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {isMobile ? 'Score progression by year' : 'Cumulative legacy score progression by year'}
        </p>
      </div>

      {/* Chart - Mobile Optimized */}
      <div style={{ width: '100%', height: isMobile ? 300 : 400 }}>
        <ResponsiveContainer>
          <LineChart 
            data={chartData}
            margin={{ 
              top: 20, 
              right: isMobile ? 15 : 30, 
              left: isMobile ? 10 : 20, 
              bottom: 20 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="Year" 
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              interval={isMobile ? 1 : 0} // Show every other year on mobile
            />
            <YAxis 
              tickFormatter={formatYAxis}
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              domain={['dataMin - 100', 'dataMax + 100']}
              width={isMobile ? 35 : 50}
            />
            <Tooltip 
              formatter={formatTooltip}
              labelStyle={{ color: '#374151', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '8px' : '12px'
              }}
              itemSorter={(item) => -item.value}
            />
            
            {/* Zero reference line */}
            <ReferenceLine 
              y={0} 
              stroke="#000000" 
              strokeDasharray="5 5" 
              opacity={0.7}
            />
            
            {/* Background lines for all other managers - Less prominent on mobile */}
            {!isMobile && otherManagers.map(manager => (
              <Line
                key={manager}
                type="monotone"
                dataKey={manager}
                stroke="#d1d5db"
                strokeWidth={2}
                strokeOpacity={0.3}
                dot={false}
                activeDot={false}
                connectNulls={false}
              />
            ))}
            
            {/* Highlighted line for selected manager */}
            <Line
              type="monotone"
              dataKey={selectedManager}
              stroke="#8b5cf6"
              strokeWidth={isMobile ? 2.5 : 3}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (!payload) return null;
                
                const change = significantChanges.find(c => c.year === payload.Year);
                if (change && !isMobile) { // Hide complex annotations on mobile
                  return (
                    <g>
                      {/* Significant change dot */}
                      <circle
                        cx={cx}
                        cy={cy}
                        r={6}
                        fill={change.change > 0 ? '#10b981' : '#ef4444'}
                        stroke="white"
                        strokeWidth={2}
                      />
                      {/* Annotation line */}
                      <line
                        x1={cx}
                        y1={cy}
                        x2={cx}
                        y2={cy - 40}
                        stroke={change.change > 0 ? '#10b981' : '#ef4444'}
                        strokeWidth={1.5}
                        strokeDasharray="2,2"
                      />
                      {/* Annotation box */}
                      <rect
                        x={cx - 35}
                        y={cy - 65}
                        width={70}
                        height={20}
                        fill={change.change > 0 ? '#10b981' : '#ef4444'}
                        rx={4}
                        opacity={0.9}
                      />
                      {/* Annotation text */}
                      <text
                        x={cx}
                        y={cy - 50}
                        textAnchor="middle"
                        fill="white"
                        fontSize="10"
                        fontWeight="bold"
                      >
                        {change.change > 0 ? '+' : ''}{Math.round(change.change)}
                      </text>
                    </g>
                  );
                } else {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isMobile ? 3 : 4}
                      fill={change && isMobile ? (change.change > 0 ? '#10b981' : '#ef4444') : '#8b5cf6'}
                      stroke="white"
                      strokeWidth={isMobile ? 1.5 : 2}
                    />
                  );
                }
              }}
              activeDot={{ r: isMobile ? 5 : 6, stroke: '#8b5cf6', strokeWidth: 2, fill: 'white' }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats - Mobile Optimized */}
      <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
        {/* Significant Changes Summary - Mobile Optimized */}
        {significantChanges.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <h4 className={`font-semibold text-gray-700 mb-3 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              Significant Legacy Score Changes
            </h4>
            <div className={`grid gap-2 sm:gap-3 ${
              isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {significantChanges
                .sort((a, b) => Math.abs(b.change) - Math.abs(a.change)) // Sort by magnitude
                .slice(0, isMobile ? 3 : 6) // Show fewer on mobile
                .map((change, index) => {
                  const getStandingsText = (standings) => {
                    if (!standings) return 'Unknown';
                    if (standings === 1) return isMobile ? '1st 🏆' : '1st Place 🏆';
                    if (standings === 2) return isMobile ? '2nd 🥈' : '2nd Place 🥈';
                    if (standings === 3) return isMobile ? '3rd 🥉' : '3rd Place 🥉';
                    if (standings >= 10) return isMobile ? `${standings}th 💩` : `${standings}th Place 💩`;
                    return isMobile ? `${standings}th` : `${standings}th Place`;
                  };

                  const getChangeContext = (change, details) => {
                    if (change > 100 && details.standings === 1) return isMobile ? 'Championship' : 'Championship Run';
                    if (change > 75 && details.standings >= 2 && details.standings <= 3) return isMobile ? 'Podium' : 'Podium Run';
                    if (change > 50 && details.wins >= 10) return isMobile ? 'Strong' : 'Strong Season';
                    if (change < -100 && details.standings >= 10) return isMobile ? 'Rough' : 'Rough Year';
                    if (change < -50 && details.wins <= 6) return isMobile ? 'Poor' : 'Disappointing';
                    return change > 0 ? (isMobile ? 'Good' : 'Good Season') : (isMobile ? 'Tough' : 'Tough Season');
                  };

                  return (
                    <div
                      key={change.year}
                      className={`p-2 sm:p-3 rounded-lg border-l-4 ${
                        change.change > 0
                          ? 'bg-green-50 border-green-500'
                          : 'bg-red-50 border-red-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                          {change.year}
                        </span>
                        <span
                          className={`font-bold ${isMobile ? 'text-sm' : 'text-sm'} ${
                            change.change > 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {change.change > 0 ? '+' : ''}{Math.round(change.change)}
                        </span>
                      </div>
                      
                      {/* Season Details - Mobile Optimized */}
                      {change.seasonDetails.hasData && (
                        <div className="mt-2 space-y-1">
                          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                            <span className="font-medium">{change.seasonDetails.record}</span>
                            {change.seasonDetails.standings && (
                              <span className="ml-2">• {getStandingsText(change.seasonDetails.standings)}</span>
                            )}
                          </div>
                          {!isMobile && change.seasonDetails.totalPF && (
                            <div className="text-xs text-gray-500">
                              {change.seasonDetails.totalPF} total points
                            </div>
                          )}
                          <div className={`font-medium ${isMobile ? 'text-xs' : 'text-xs'} ${
                            change.change > 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {getChangeContext(change.change, change.seasonDetails)}
                          </div>
                        </div>
                      )}
                      
                      <div className={`text-gray-400 mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        {Math.round(change.prevScore)} → {Math.round(change.score)}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Overall Stats - Mobile Optimized */}
        <div className={`grid gap-3 sm:gap-4 text-center ${
          isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'
        }`}>
          {(() => {
            const managerData = chartData.map(d => d[selectedManager]).filter(v => v !== null && v !== undefined);
            if (managerData.length === 0) return null;
            
            const currentScore = managerData[managerData.length - 1];
            const startScore = managerData[0];
            
            // Find peak score and its year
            const maxScore = Math.max(...managerData);
            const maxScoreIndex = managerData.findIndex(score => score === maxScore);
            const maxScoreYear = chartData[maxScoreIndex]?.Year;
            
            // Find lowest score and its year
            const minScore = Math.min(...managerData);
            const minScoreIndex = managerData.findIndex(score => score === minScore);
            const minScoreYear = chartData[minScoreIndex]?.Year;
            
            return (
              <>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <div className={`font-bold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {Math.round(currentScore)}
                  </div>
                  <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                    Current Score
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                  <div className={`font-bold ${isMobile ? 'text-base' : 'text-lg'} ${
                    currentScore > startScore ? 'text-green-600' : 
                    currentScore < startScore ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {currentScore > startScore ? '+' : ''}{Math.round(currentScore - startScore)}
                  </div>
                  <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                    Total Change
                  </div>
                </div>
                {!isMobile && (
                  <>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className={`font-bold text-green-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {Math.round(maxScore)}
                      </div>
                      <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        Peak Score {maxScoreYear ? `(${maxScoreYear})` : ''}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className={`font-bold text-red-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {Math.round(minScore)}
                      </div>
                      <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                        Lowest Score {minScoreYear ? `(${minScoreYear})` : ''}
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default LegacyScoreChart;